/**
 * PR-K (Q3.A) — renderer-backend telemetry channel.
 *
 * # Why this exists
 *
 * After PR-K flips the default renderer to WebGL we need a way to
 * answer "what fraction of users are on WebGPU vs WebGL2 vs WebGL1?"
 * without leaning on a server-side rollout. The plan §Q3 calls for a
 * single-counter event emitted once per session at app boot, carrying
 * the resolved renderer choice and the entity count after the first
 * import settles.
 *
 * # Why a tiny standalone module (not the audit reducer)
 *
 * The existing audit channel (`editorStore.ts:pushAuditEvent`) is
 * keyed off `EditorCommand.commandType`. Telemetry is not an editor
 * command — there's no user intent, no reducer state mutation, no
 * undo/redo entry. Pushing it as a synthetic command would dilute the
 * audit log (every session restart would shift the latest 24 events
 * by one) and force a contract change in the `editor.ts` Zod schema.
 *
 * Instead we use a fire-and-forget side channel:
 *   1. `console.log` so production browser sessions surface the value
 *      to anyone with DevTools open (mirrors createRenderer.ts:128).
 *   2. `window.dispatchEvent(new CustomEvent(...))` so downstream
 *      collectors (e.g. a Sentry integration, a custom analytics
 *      bootstrap) can subscribe without monkey-patching the store.
 *   3. Idempotency guard so React StrictMode double-invokes don't
 *      double-emit. The session is over the first time we see a
 *      non-zero entity count (post first DXF import).
 *
 * # Event shape
 *
 *   {
 *     type: "session.renderer",
 *     renderer: "webgpu" | "webgl2" | "webgl1",
 *     entityCountAtImport: number,
 *     timestampMs: number,
 *   }
 *
 * `renderer` mirrors `RendererBackend` from
 * `apps/web/src/app/scene3d/createRenderer.ts`. Tests can stub the
 * window event listener to assert on the payload.
 */

import type { RendererBackend } from "../scene3d/createRenderer";

export const SESSION_RENDERER_EVENT = "edit-dwg.telemetry.session-renderer";

export interface SessionRendererTelemetry {
  type: "session.renderer";
  renderer: RendererBackend;
  entityCountAtImport: number;
  timestampMs: number;
}

let alreadyEmitted = false;

/**
 * Reset the once-per-session guard. Test-only — production code path
 * relies on the module-level latch surviving every React mount.
 */
export function resetSessionRendererTelemetryForTest(): void {
  alreadyEmitted = false;
}

/**
 * Emit the session.renderer counter. Idempotent — second call within
 * the same module load is a no-op.
 *
 * The function is intentionally synchronous (no async / await) so the
 * caller can drop it into a `useEffect` without the cleanup-promise
 * dance.
 */
export function emitSessionRendererTelemetry(payload: {
  renderer: RendererBackend;
  entityCountAtImport: number;
}): void {
  if (alreadyEmitted) return;
  alreadyEmitted = true;

  const event: SessionRendererTelemetry = {
    type: "session.renderer",
    renderer: payload.renderer,
    entityCountAtImport: payload.entityCountAtImport,
    timestampMs: Date.now(),
  };

  // Channel 1: console — visible in DevTools, scrapable by automation.
  // Tagged identically to PR-A's createRenderer log so a single
  // [scene3d] grep covers both events.
  // eslint-disable-next-line no-console
  console.log(
    `[scene3d] session.renderer renderer=${event.renderer} ` +
      `entities=${event.entityCountAtImport} ts=${event.timestampMs}`,
  );

  // Channel 2: DOM custom event — downstream collectors subscribe via
  // `window.addEventListener(SESSION_RENDERER_EVENT, ...)` without
  // having to monkey-patch the editor store.
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(SESSION_RENDERER_EVENT, { detail: event }),
      );
    } catch {
      // CustomEvent missing on very old runtimes — swallow; the
      // console channel still carries the data.
    }
  }
}
